import React, { useRef, useCallback, useEffect, useState } from 'react';
import './RichTextEditor.css'; // Custom styles

interface RichTextEditorProps {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  className?: string;
  readOnly?: boolean;
  minimal?: boolean; // For a more minimal toolbar
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Enter text...',
  className = '',
  readOnly = false,
  minimal = false
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  
  // Update the editor content when value prop changes
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      // If content is empty or just contains empty tags, pass undefined to make it truly optional
      const cleanContent = content.replace(/<[^>]*>/g, '').trim();
      onChange(cleanContent === '' ? undefined : content);
    }
  }, [onChange]);

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);

  const execCommand = useCallback((command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  }, [handleInput]);

  const insertLink = useCallback(() => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const selectedText = selection.toString();
      if (selectedText.trim() === '') {
        alert('Please select some text first');
        return;
      }
      
      const url = prompt('Enter the URL:', 'https://');
      if (url && url.trim() !== '') {
        execCommand('createLink', url);
      }
    } else {
      alert('Please select some text first');
    }
  }, [execCommand]);

  const removeFormat = useCallback(() => {
    execCommand('removeFormat');
  }, [execCommand]);

  if (readOnly) {
    return (
      <div 
        className={`rich-text-content ${className}`}
        dangerouslySetInnerHTML={{ __html: value || '' }}
      />
    );
  }

  return (
    <div className={`rich-text-editor ${className} ${isFocused ? 'focused' : ''}`}>
      {/* Toolbar */}
      <div className="rich-text-toolbar">
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => execCommand('bold')}
          className="toolbar-btn"
          title="Bold"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => execCommand('italic')}
          className="toolbar-btn"
          title="Italic"
        >
          <em>I</em>
        </button>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => execCommand('underline')}
          className="toolbar-btn"
          title="Underline"
        >
          <u>U</u>
        </button>
        <span className="toolbar-divider">|</span>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={insertLink}
          className="toolbar-btn"
          title="Insert Link"
        >
          🔗
        </button>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={removeFormat}
          className="toolbar-btn"
          title="Remove Formatting"
        >
          ✕
        </button>
      </div>
      
      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable={!readOnly}
        onInput={handleInput}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className="rich-text-content-editable"
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />
    </div>
  );
};

export default RichTextEditor;
